











import {Vue, Watch} from "vue-property-decorator";
import Component from "vue-class-component";
import {Getter} from "vuex-class";
import {Route, RouteConfig} from "vue-router";
import _ from "lodash";
import {Feature} from "@/modules/Feature";
import Owner from "@/model/cospirit-api/Owner";
import {User} from "front-component/modules/User";
import {ptfFeatures} from "@/Constants";

@Component
export default class LoginRedirect extends Vue {
    @Getter("getCurrentOwner") public currentOwner!: Owner;
    @Getter("getCurrentFeatures") public currentFeatures!: Feature[];
    @Getter("getCurrentUser") public currentUser!: User;
    @Getter("hasFeature") public hasFeature!: (feature: string) => boolean;
    @Getter("getErrorOwner") public errorOwner!: boolean;

    protected loading: boolean = true;
    private errorMsg: string = "noActivatedFeature";

    public mounted() {
        this.noOwner();
    }

    @Watch("currentOwner")
    protected changedOwner(): void {
        this.redirectToDefaultFeature();
    }

    @Watch("errorOwner")
    protected changedErrorOwner(): void {
        this.noOwner();
    }

    public beforeRouteEnter(to: Route, from: Route, next: any) {
        next((vm: LoginRedirect) => {
            vm.redirectToDefaultFeature();
        });
    }

    public beforeRouteUpdate(to: Route, from: Route, next: any) {
        next();
        this.redirectToDefaultFeature();
    }

    protected redirectToDefaultFeature(): void {
        this.loading = true;
        if (!this.currentOwner) {
            this.noOwner();
            return;
        }

        const allFlattenRoutes: RouteConfig[] = this.getFlattenRoutes();
        const hiddenFeaturesToCheck = [
            { [ptfFeatures.request]: "HIDE_REQUEST" },
            { [ptfFeatures.billing]: "HIDE_BILLING" },
            { [ptfFeatures.report]: "HIDE_REPORT" },
            { [ptfFeatures.zero_waste]: "HIDE_ZERO_WASTE" },
            { [ptfFeatures.homepage]: "HIDE_HOMEPAGE" },
            { [ptfFeatures.socle]: "HIDE_SOCLE" },
            { [ptfFeatures.socle]: "HIDE_SOCLE_VALIDATION" },
        ];

        // Redirect to the first available page if the default page is hidden
        for (const feature of hiddenFeaturesToCheck) {
            const [featureCode, featureHideCode] = Object.entries(feature)[0];
            if (this.redirectIfDefaultPageHidden(featureCode, featureHideCode, allFlattenRoutes)) {
                return;
            }
        }

        // Redirect to the default page
        for (const feature of this.currentFeatures) {
            const newTargetedRoute = _.find(allFlattenRoutes, (possibleRoute: RouteConfig): boolean => {
                const routeFeature = possibleRoute.meta?.feature || "";
                const isADefaultRoute = Boolean(possibleRoute.meta?.default);
                return (
                    isADefaultRoute
                    && (!this.isDefaultPageAvailable() || routeFeature === this.currentOwner.defaultPage)
                    && routeFeature === feature.code
                );
            });

            if (newTargetedRoute?.name) {
                this.$router.push({ name: newTargetedRoute.name });
                return;
            }
        }

        this.noFeatureToRedirect();
    }

    protected redirectIfDefaultPageHidden(
        featureCode: string,
        featureHideCode: string,
        allFlattenRoutes: RouteConfig[]
    ): boolean {
        const featureIsVisible = this.hasFeature(featureCode) && !_.some(this.currentUser.userHideFeatures, (line) => {
            return line.code === featureHideCode;
        });

        if (this.isDefaultPageAvailable() && this.currentOwner.defaultPage === featureCode && !featureIsVisible) {
            for (const feature of this.currentFeatures) {
                const newTargetedRoute = allFlattenRoutes.find((possibleRoute: RouteConfig) =>
                    possibleRoute.meta?.feature === feature.code && feature.code !== featureCode
                );

                if (newTargetedRoute?.name) {
                    this.$router.push({ name: newTargetedRoute.name });
                    return true;
                }
            }
        }

        return false;
    }

    private isDefaultPageAvailable(): boolean {
        return _.some(this.currentFeatures, (feature) => {
            return feature.code === this.currentOwner.defaultPage;
        });
    }

    private getFlattenRoutes(): RouteConfig[] {
        return _.flatMap(
            // Turn around to retrieve all routes ("options" key is not public in VueRouter object)
            _.get(this.$router, "options.routes", []),
            (routeConfig: RouteConfig): RouteConfig[] => {
                if (_.has(routeConfig, "children")) {
                    return _.get(routeConfig, "children", []);
                }

                return [routeConfig];
            }
        );
    }

    protected noFeatureToRedirect() {
        if (this.currentOwner) {
            this.errorMsg = "noActivatedFeature";
            this.loading = false;
        }
    }

    protected noOwner() {
        if (this.errorOwner) {
            this.errorMsg = "noOwner";
            this.loading = false;
        }
    }
}
